import { useState } from 'react';
import { operationsApi, type AssetType } from '../api';

type Props = {
  assetTypes: AssetType[];
  onClose: () => void;
  onCreated: () => void;
};

export function AssetCreateModal({ assetTypes, onClose, onCreated }: Props) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState(assetTypes[0]?.slug ?? 'generic');
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState<string>('');
  const [vin, setVin] = useState('');
  const [fuelType, setFuelType] = useState<string>('');
  const [capacity, setCapacity] = useState<string>('');
  const [capacityUnit, setCapacityUnit] = useState('L');
  const [odometer, setOdometer] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await operationsApi.createAsset({
        code: code.trim(),
        name: name.trim(),
        asset_type_slug: type,
        plate_number: plate || null,
        make: make || null,
        model: model || null,
        year: year ? Number(year) : null,
        vin: vin || null,
        fuel_type: fuelType || null,
        capacity_value: capacity ? Number(capacity) : null,
        capacity_unit: capacity ? capacityUnit : null,
        odometer_current: odometer ? Number(odometer) : null,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Failed to create asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={onSubmit}
        className="card w-full max-w-2xl space-y-4 bg-surface p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add Asset</h2>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {error && (
          <div className="rounded bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{error}</div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code *">
            <input
              required
              className="input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="VEH-001"
            />
          </Field>
          <Field label="Name *">
            <input
              required
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Hilux #1"
            />
          </Field>
          <Field label="Type">
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              {assetTypes.length === 0 && <option value="generic">generic</option>}
              {assetTypes.map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Plate">
            <input className="input" value={plate} onChange={(e) => setPlate(e.target.value)} />
          </Field>
          <Field label="Make">
            <input className="input" value={make} onChange={(e) => setMake(e.target.value)} />
          </Field>
          <Field label="Model">
            <input className="input" value={model} onChange={(e) => setModel(e.target.value)} />
          </Field>
          <Field label="Year">
            <input
              type="number"
              className="input"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </Field>
          <Field label="VIN">
            <input className="input" value={vin} onChange={(e) => setVin(e.target.value)} />
          </Field>
          <Field label="Fuel type">
            <select
              className="input"
              value={fuelType}
              onChange={(e) => setFuelType(e.target.value)}
            >
              <option value="">—</option>
              {['DIESEL', 'PETROL', 'EV', 'HYBRID', 'LPG', 'CNG'].map((f) => (
                <option key={f}>{f}</option>
              ))}
            </select>
          </Field>
          <Field label="Odometer (km)">
            <input
              type="number"
              className="input"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
            />
          </Field>
          <Field label="Tank capacity">
            <input
              type="number"
              className="input"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </Field>
          <Field label="Unit">
            <select
              className="input"
              value={capacityUnit}
              onChange={(e) => setCapacityUnit(e.target.value)}
            >
              {['L', 'gal', 'tons', 'seats', 'm3'].map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Creating…' : 'Create Asset'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
